<template>
  <cs-preloader-overlay :loading="loading">
    <div class="identity-screen" :style="{ backgroundImage }">
      <div class="main-container">
        <div class="title-container" :class="{ approved: isApproved }">
          <template v-if="isApproved">
            <capusta-title icon-outside />
            <div class="approved-title">
              <div class="approved-status">{{ customerTitle }}</div>
              <div class="approved-info">{{ customerInfo }}</div>
            </div>
          </template>
          <capusta-title v-else :title="$t('common.identification')" icon-outside>
            <template slot="description">
              <div class="identity-description" v-html="$t('raise.transfer.limits.and.unblock.payout')"></div>
            </template>
          </capusta-title>
        </div>

        <div class="content-container">
          <identity-awaiting v-if="isAwaitingVerification" />
          <identity-approved v-else-if="isApproved" @reset="onReset" />
          <identity-form
            v-else
            :value="identification"
            :countries="countries"
            @change-country="onChangeCountry"
            @submit="onSend"
          />
        </div>
      </div>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import {
  loadCountries,
  loadIdentification,
  sendIdentification
} from 'services/identification';

import CapustaTitle from 'modules/CapustaLayout/CapustaTitle';

import { formatDateToCustomDateFormat } from 'lib/utils/date';

import IdentityAwaiting from './IdentityAwaiting';
import IdentityApproved from './IdentityApproved';
import IdentityForm from './IdentityForm';

import { EVENT_BUS } from '@/eventBus';
import { IDENTIFICATION_STATUS_CHANGE } from '@/const';
import {
  APPROVED,
  AWAITING_VERIFICATION,
  DEFAULT_COUNTRY,
  DEFAULT_IDENTIFICATION
} from './const';

export default {
  name: 'ProfileIdentity',
  components: {
    CapustaTitle,
    IdentityAwaiting,
    IdentityApproved,
    IdentityForm,
  },
  async created() {
    EVENT_BUS.$on(IDENTIFICATION_STATUS_CHANGE, async () => {
      await this.loadData();
    });

    await this.loadData();
  },
  data() {
    return {
      loading: false,
      countries: [],
      identification: DEFAULT_IDENTIFICATION,
    };
  },
  computed: {
    isAwaitingVerification() {
      if (this.$validate.isNull(this.identification)) {
        return false;
      }

      return this.identification.status === AWAITING_VERIFICATION;
    },
    isApproved() {
      if (this.$validate.isNull(this.identification)) {
        return false;
      }

      return this.identification.status === APPROVED;
    },
    backgroundImage() {
      if (this.isApproved) {
        return `url(${require('@/assets/svg/identification/approved.svg')})`;
      }

      if (this.isAwaitingVerification) {
        return `url(${require('@/assets/svg/identification/awaiting.svg')})`;
      }

      return `url(${require('@/assets/svg/identification/need.svg')})`;
    },
    customerTitle() {
      return this.$t('identification.passed');
    },
    customerInfo() {
      return `${this.identification.firstName}\u00A0${this.identification.secondName}`;
    },
  },
  methods: {
    async loadData() {
      this.loading = true;

      try {
        const countries = await loadCountries();

        this.countries = countries;
      } catch {
        this.$alert({
          type: 'warning',
          text: 'identification.data.not.loaded',
        });

        return;
      }

      try {
        const identification = await loadIdentification();
        const { country, birthDate, documentIssueDate } = identification;

        this.identification = identification;
        this.identification.birthDate = formatDateToCustomDateFormat(birthDate, 'DD.MM.YYYY');
        this.identification.documentIssueDate = formatDateToCustomDateFormat(documentIssueDate, 'DD.MM.YYYY');

        if (this.$validate.isNull(country)) {
          this.identification.country = DEFAULT_COUNTRY;
        }
      } catch (error) {
        this.onReset();
      }

      this.loading = false;
    },
    async onSend(data) {
      this.loading = true;

      try {
        await sendIdentification(data);
        this.identification.status = AWAITING_VERIFICATION;
      } catch {
        this.onReset();
      }
      this.loading = false;
    },
    onReset() {
      this.identification = DEFAULT_IDENTIFICATION;
    },
    onChangeCountry(country) {
      this.identification.country = country;
    },
  },
};
</script>

<style lang="sass" scoped>
  .identity-screen
    width: 100%
    max-width: 760px
    margin: 0 auto
    background-position: top -20px right -20px
    background-repeat: no-repeat
    background-size: 146px 180px

    @media screen and (min-width: 710px)
      background-position: top 30px right 0
      background-size: 260px 320px

  .identity-description
    width: 300px

    &::v-deep span
      display: block

      +respond-to('small')
        display: inline

  .main-container
    width: 100%
    max-width: 500px
    position: relative
    margin: 0 auto
    padding: 0 0 40px
    overflow: hidden

    +respond-to('small')
      padding: 40px 0px 70px
      overflow: visible

  .title-container
    position: relative
    padding: 20px

    &.approved
      max-width: calc(100% - 110px)
      margin-bottom: 60px

      +respond-to('small')
        max-width: 365px
        margin-bottom: 0

    & .approved-title
      height: 60px
      display: flex
      flex-direction: column

      +respond-to('medium')
        justify-content: center

  .content-container
    *
      &:not(:last-child)
        margin-bottom: 10px

  .background-icon-container
    width: 146px
    height: 180px
    position: absolute
    top: -20px
    right: -20px
    font-size: 0
    z-index: -1

    *
      width: 100%
      height: 100%

    +respond-to('small')
      width: auto
      height: auto
      top: 60px
      right: -112px

  .approved-status
    font-weight: 500
    font-size: 12px
    line-height: 24px
    color: #7CB342
    white-space: nowrap

  .approved-info
    font-weight: 500
    font-size: 20px
    line-height: 30px
    color: #fff
    word-break: break-word
</style>

<style lang="sass">
  .identity-screen .title-container > .capusta-title .title-container
    padding: 0
</style>
